import React from 'react';
import PropTypes from 'prop-types';
import { poolMemberKey } from '../../utils/buildPoolEngineInput';

import { OVERSUBSCRIBED_LABEL, OVERSUBSCRIBED_BODY, UNALLOCATED_BODY } from '../../../../shared/poolOversubscribed';
import { describeCapOverflow, hasCapOverflow } from '../../../../shared/poolCapOverflow';
import { describePoolShareOfGross, describeTakeShares } from '../../../../shared/poolShareOfGross';

const SimplePoolSplitCard = ({
  poolMembers, poolAmount, grossPotential, dealBracketReached, oversubscribed, unallocated, lineup, formatCalculatedNumber,
}) => {
  if (!poolMembers || poolMembers.length === 0) return null;

  const earnings = { poolAmount, grossPotential, dealBracketReached };
  const poolShareOfGrossNote = describePoolShareOfGross(earnings, { formatAmount: formatCalculatedNumber });

  const byKey = new Map(poolMembers.map((m) => [m.performerId, m]));
  const nameByKey = new Map((lineup || []).map((member) => [poolMemberKey(member), member.performer_name]));
  const rows = (lineup || []).map((member) => {
    const key = poolMemberKey(member);
    const result = byKey.get(key);
    return {
      id: member.id,
      name: member.performer_name || 'Unnamed artist',
      take: result ? result.take : 0,
      shares: result ? describeTakeShares(result.take, earnings) : null,
      capped: !!(result && result.capped),
      floored: !!(result && result.floored),
      capOverflowNote: result && result.capped && hasCapOverflow(result)
        ? describeCapOverflow(result, { nameFor: (performerId) => nameByKey.get(performerId), formatAmount: formatCalculatedNumber })
        : null,
    };
  });

  return (
    <div className="simple-offer-card simple-pool-split-card" data-testid="simple-pool-split-card">
      <div className="simple-offer-card__header">
        <div>
          <h3 className="simple-offer-card__title">Pool split</h3>
          <p className="simple-offer-card__subtitle">
            Pool total {formatCalculatedNumber(poolAmount)}
            {poolShareOfGrossNote && (
              <span data-testid="simple-pool-split-gross">{` = ${poolShareOfGrossNote}`}</span>
            )}
          </p>
        </div>
      </div>

      {oversubscribed && (
        <div className="simple-pool-split-card__warning" data-testid="simple-pool-split-warning">
          <strong>{OVERSUBSCRIBED_LABEL}</strong> — {OVERSUBSCRIBED_BODY}
        </div>
      )}

      {/* Counterpart to the oversubscribed warning — see V3PoolSplitPreview. */}
      {!oversubscribed && unallocated > 0 && (
        <div className="simple-pool-split-card__unallocated" data-testid="simple-pool-split-unallocated">
          <strong>{formatCalculatedNumber(unallocated)} unallocated</strong> — {UNALLOCATED_BODY}
        </div>
      )}

      {rows.map((row) => (
        <div key={row.id} className="simple-pool-split-card__row" data-testid="simple-pool-split-row">
          <span className="simple-pool-split-card__name">{row.name}</span>
          <span className="simple-pool-split-card__take">
            {formatCalculatedNumber(row.take)}
            {row.capped && <span className="simple-pool-split-card__flag simple-pool-split-card__flag--cap">capped</span>}
            {row.floored && <span className="simple-pool-split-card__flag simple-pool-split-card__flag--floor">floored</span>}
          </span>
          {row.shares && (
            <div className="simple-pool-split-card__shares" data-testid="simple-pool-split-shares">{row.shares}</div>
          )}
          {row.capOverflowNote && (
            <div className="simple-pool-split-card__overflow" data-testid="simple-pool-split-overflow">
              Cap overflow: {row.capOverflowNote}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

SimplePoolSplitCard.propTypes = {
  poolMembers: PropTypes.arrayOf(PropTypes.object),
  poolAmount: PropTypes.number,
  grossPotential: PropTypes.number,
  dealBracketReached: PropTypes.shape({ number: PropTypes.number, bracketCount: PropTypes.number }),
  oversubscribed: PropTypes.bool,
  unallocated: PropTypes.number,
  lineup: PropTypes.arrayOf(PropTypes.object),
  formatCalculatedNumber: PropTypes.func.isRequired,
};

SimplePoolSplitCard.defaultProps = {
  poolMembers: [],
  poolAmount: 0,
  grossPotential: 0,
  dealBracketReached: null,
  oversubscribed: false,
  unallocated: 0,
  lineup: [],
};

export default SimplePoolSplitCard;
